import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment/min/moment-with-locales";
import { ArrowLeft, Check, CheckCheck, Loader2, Send } from "lucide-react";
import {
  GetChatRoomsAPI,
  GetMessagesAPI,
  MarkChatReadAPI,
} from "@/services/api/chat/getChatRooms";
import { GetBusinessPlanAPI } from "@/services/api/business/getBusinessPlan";
import socket from "@/services/config/socket.io/socket.io";
import { registerPresence } from "@/services/middleware/chatHelper";
import type { ChatMessage, ChatRoom } from "@/utils/chatFormat";
import {
  CHAT_TYPE_LABEL_KEYS,
  formatMessageTime,
  getMessageDayKey,
  getRoomIcon,
  getRoomPeer,
  getRoomTitle,
  isGroupRoom,
} from "@/utils/chatFormat";
import { withMomentLocale } from "@/utils/eventFormat";
import { getAuthUserId } from "@/utils/authUser";
import ChatAvatar from "@/components/student/ChatAvatar";
import AdminEmptyState from "@/components/AdminEmptyState";

type ThreadItem =
  | { key: string; kind: "date"; label: string }
  | { key: string; kind: "message"; message: ChatMessage };

type PresenceUpdate = {
  user_id?: string;
  is_online?: boolean;
  last_seen?: number | string | null;
};

type StudentChatMessagePageProps = {
  /** Where the back arrow goes — the business portal reuses this thread. */
  backPath?: string;
};

/**
 * Message thread — the web port of the RN `MessageScreen`. The room is joined
 * over the socket (`joinRoom`), history comes from the REST endpoint, and new
 * messages arrive on the room's own socket event (named by the chat_room_id).
 * The peer's online status follows the `presence_update` broadcast.
 */
const StudentChatMessagePage: React.FC<StudentChatMessagePageProps> = ({
  backPath = "/student/chat",
}) => {
  const { t } = useTranslation();
  const goBack = useGoBack(backPath);
  const { chatRoomId = "" } = useParams<{ chatRoomId: string }>();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  const actingUserId: string = useSelector((state: any) =>
    getAuthUserId(state.auth),
  );
  const userAuth: string = useSelector(
    (state: any) => state.auth?.user_auth ?? "",
  );
  withMomentLocale(language);

  const [room, setRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [senderNames, setSenderNames] = useState<Record<string, string>>({});
  const [peerPresence, setPeerPresence] = useState<{
    is_online: boolean;
    last_seen: number | string | null;
  } | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(socket.connected || socket.active);
  // Business subscriptions that ended (`access_status === "locked"`) may
  // still read and receive chat, but the composer is disabled up-front.
  const [subscriptionLocked, setSubscriptionLocked] = useState(false);
  // Server-side denial (`sendMessageDenied` event) — the server is the
  // source of truth, so any denial disables the composer and shows why.
  const [sendDeniedMessage, setSendDeniedMessage] = useState<string | null>(
    null,
  );
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!chatRoomId) return;
    let cancelled = false;
    const load = async () => {
      const roomsResp = await GetChatRoomsAPI();
      if (cancelled) return;
      if (roomsResp?.status === 0 && Array.isArray(roomsResp.result)) {
        const found = (roomsResp.result as ChatRoom[]).find(
          (item) => item.id === chatRoomId,
        );
        if (found) {
          setRoom(found);
          const names: Record<string, string> = {};
          (found.participants ?? []).forEach((participant) => {
            if (participant.user_id && participant.name) {
              names[participant.user_id] = participant.name;
            }
          });
          setSenderNames(names);
          const peer = isGroupRoom(found)
            ? null
            : getRoomPeer(found, actingUserId);
          setPeerPresence(
            peer
              ? {
                  is_online: !!peer.is_online,
                  last_seen: peer.last_seen ?? null,
                }
              : null,
          );
        }
      }

      const messagesResp = await GetMessagesAPI(chatRoomId, 1);
      if (cancelled) return;
      if (messagesResp?.status === 0 && Array.isArray(messagesResp.result)) {
        setMessages(messagesResp.result as ChatMessage[]);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [chatRoomId, actingUserId]);

  useEffect(() => {
    if (!chatRoomId || !actingUserId) return;
    MarkChatReadAPI({ chat_room_id: chatRoomId, user_id: actingUserId });
  }, [chatRoomId, actingUserId]);

  // Business sessions already know (or can cheaply learn) the plan state:
  // a locked plan disables the composer up-front. Students skip the fetch.
  useEffect(() => {
    if (userAuth !== "business") return;
    let cancelled = false;
    const load = async () => {
      const resp = await GetBusinessPlanAPI();
      if (cancelled) return;
      if (resp?.status === 0 && resp.result?.access_status === "locked") {
        setSubscriptionLocked(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userAuth]);

  useEffect(() => {
    if (!chatRoomId) return;
    if (actingUserId) registerPresence(actingUserId);
    if (!socket.connected) socket.connect();

    const onConnect = () => {
      setConnected(true);
      socket.emit("joinRoom", {
        chat_room_id: chatRoomId,
        user_id: actingUserId,
      });
    };
    const onDisconnect = () => setConnected(false);
    const onNewMessage = (chatMessage: ChatMessage) => {
      if (!chatMessage?.id) return;
      setMessages((prev) =>
        prev.some((item) => item.id === chatMessage.id)
          ? prev
          : [chatMessage, ...prev],
      );
    };
    const onSendMessageDenied = (payload: {
      chat_room_id?: string;
      status?: number;
      message?: string;
    }) => {
      if (payload?.chat_room_id && payload.chat_room_id !== chatRoomId) return;
      // The server never created/broadcast the message, so no bubble was
      // added optimistically — just surface the reason inline.
      setSendDeniedMessage(
        payload?.message || t("Your subscription has ended. Please renew to continue."),
      );
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on(chatRoomId, onNewMessage);
    socket.on("sendMessageDenied", onSendMessageDenied);
    if (socket.connected) {
      socket.emit("joinRoom", {
        chat_room_id: chatRoomId,
        user_id: actingUserId,
      });
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off(chatRoomId, onNewMessage);
      socket.off("sendMessageDenied", onSendMessageDenied);
      socket.disconnect();
    };
  }, [chatRoomId, actingUserId]);

  const peerId = useMemo(() => {
    if (!room || isGroupRoom(room)) return null;
    return getRoomPeer(room, actingUserId)?.user_id ?? null;
  }, [room, actingUserId]);

  useEffect(() => {
    if (!peerId) return;
    const onPresenceUpdate = (data: PresenceUpdate) => {
      if (data?.user_id !== peerId) return;
      setPeerPresence({
        is_online: !!data.is_online,
        last_seen: data.last_seen ?? null,
      });
    };
    socket.on("presence_update", onPresenceUpdate);
    return () => {
      socket.off("presence_update", onPresenceUpdate);
    };
  }, [peerId]);

  const items = useMemo<ThreadItem[]>(() => {
    const built: ThreadItem[] = [];
    let lastDay: string | null = null;
    [...messages].reverse().forEach((message) => {
      const day = getMessageDayKey(message.ctime);
      if (day !== lastDay) {
        let label = "";
        if (message.ctime) {
          const parsed = moment(message.ctime);
          if (parsed.isSame(moment(), "day")) {
            label = t("Today");
          } else if (parsed.isSame(moment().subtract(1, "day"), "day")) {
            label = t("Yesterday");
          } else {
            label = parsed.format("MMM D, YYYY");
          }
        }
        built.push({ key: `date-${day}`, kind: "date", label });
        lastDay = day;
      }
      built.push({ key: message.id, kind: "message", message });
    });
    return built;
  }, [messages, t]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [items.length, loading]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 128)}px`;
  }, [draft]);

  const composerDisabled = subscriptionLocked || !!sendDeniedMessage;
  // The denial message is an ERROR_CODE string the server sent verbatim;
  // `t` translates it where a resource exists and returns it as-is otherwise.
  const composerNoticeKey =
    sendDeniedMessage || "Your subscription has ended. Please renew to continue.";

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed || !chatRoomId || !actingUserId || composerDisabled) return;
    socket.emit("sendMessage", {
      chat_room_id: chatRoomId,
      sender_id: actingUserId,
      message: trimmed,
      type: "text",
    });
    setDraft("");
  };

  const lastSeenLabel = (lastSeen: number | string | null | undefined) => {
    if (!lastSeen) return t("Offline");
    const parsed = moment(lastSeen);
    if (!parsed.isValid()) return t("Offline");
    const now = moment();
    if (now.diff(parsed, "seconds") < 60) return t("Last seen just now");
    if (parsed.isSame(now, "day")) {
      return t("Last seen today at {{time}}", { time: parsed.format("HH:mm") });
    }
    if (parsed.isSame(now.clone().subtract(1, "day"), "day")) {
      return t("Last seen yesterday at {{time}}", {
        time: parsed.format("HH:mm"),
      });
    }
    return t("Last seen {{date}} at {{time}}", {
      date: parsed.format("MM/DD"),
      time: parsed.format("HH:mm"),
    });
  };

  const isGroup = isGroupRoom(room);
  const roomTitle = getRoomTitle(room, actingUserId) || t("Chat");
  const isOnline = peerPresence?.is_online ?? false;
  const typeLabelKey = CHAT_TYPE_LABEL_KEYS[room?.type ?? ""];
  const showSenderName = room?.type === "group" || room?.type === "admin";
  const headerSubtitle = isGroup
    ? t(typeLabelKey || "Chat")
    : peerPresence
      ? isOnline
        ? t("Online")
        : lastSeenLabel(peerPresence.last_seen)
      : "";

  return (
    <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-4 py-4">
      <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
        <button
          type="button"
          onClick={goBack}
          aria-label={t("Back")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#fff3e0] hover:text-[#ffa217]"
        >
          <ArrowLeft size={20} />
        </button>
        <ChatAvatar
          name={roomTitle}
          imageUrl={getRoomIcon(room, actingUserId)}
          size="sm"
          isOnline={peerPresence ? isOnline : null}
        />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-bold text-gray-900">
            {roomTitle}
          </h1>
          {headerSubtitle && (
            <p
              className={`truncate text-xs ${
                !isGroup && isOnline ? "text-[#25D366]" : "text-gray-500"
              }`}
            >
              {headerSubtitle}
            </p>
          )}
        </div>
        {!connected && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
            {t("Reconnecting...")}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-1 flex-col overflow-y-auto rounded-2xl border border-gray-100 bg-white px-3 py-2 shadow-sm">
        {loading ? (
          <div className="flex h-full items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-[#ffa217]" />
          </div>
        ) : items.length === 0 ? (
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="chat-outline"
            title={t("No messages yet")}
            message={t("Send a message to start the conversation.")}
          />
        ) : (
          <div className="mt-auto flex flex-col gap-0.5 py-2">
            {items.map((item) => {
              if (item.kind === "date") {
                return (
                  <div key={item.key} className="my-3 flex justify-center">
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-500">
                      {item.label}
                    </span>
                  </div>
                );
              }

              const message = item.message;
              const isMine = message.sender_id === actingUserId;
              const senderName = senderNames[message.sender_id];
              const linkUrl = message.metadata?.url || "";
              const isLink = message.type === "link";
              const bubbleClass = isMine
                ? "bg-[#ffa217] text-white"
                : "bg-gray-100 text-gray-900";
              const bubbleTail = isMine ? "rounded-br-md" : "rounded-bl-md";
              const timeClass = isMine ? "text-white/70" : "text-gray-400";

              return (
                <div
                  key={item.key}
                  className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                >
                  <div className="flex max-w-[74%] flex-col">
                    {!isMine && showSenderName && senderName && (
                      <span className="mb-0.5 ml-3 text-[11px] text-gray-400">
                        {senderName}
                      </span>
                    )}
                    {isLink && linkUrl ? (
                      <a
                        href={linkUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`rounded-2xl px-3.5 py-2 text-sm no-underline ${bubbleClass}`}
                      >
                        <span className="font-semibold">
                          {message.message || linkUrl}
                        </span>
                        <span className="mt-1 block truncate text-xs opacity-80">
                          {linkUrl}
                        </span>
                      </a>
                    ) : (
                      <div
                        className={`whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm ${bubbleClass} ${bubbleTail}`}
                      >
                        {message.message}
                      </div>
                    )}
                    <span
                      className={`mt-0.5 flex items-center gap-1 px-1 text-[10px] ${timeClass}`}
                    >
                      {formatMessageTime(message.ctime)}
                      {isMine &&
                        (message.read_by_receiver ? (
                          <CheckCheck size={12} />
                        ) : (
                          <Check size={12} />
                        ))}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="flex items-end gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            disabled={composerDisabled}
            placeholder={t("Type a message...")}
            className="max-h-32 min-h-[42px] flex-1 resize-none rounded-full bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-[#ffa217]/40 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || composerDisabled}
            aria-label={t("Send")}
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-[#ffa217] text-white transition hover:bg-[#f59200] disabled:opacity-60"
          >
            <Send size={16} />
          </button>
        </div>
        {composerDisabled && (
          <p className="mt-1.5 px-2 text-xs font-medium text-red-500">
            {t(composerNoticeKey)}
          </p>
        )}
      </div>
    </div>
  );
};

export default StudentChatMessagePage;
